import { inject, Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { formatNumericalMonthToMM } from '../../../../../../../core/functions/date-helpers';
import { CardOnFileResponse } from '../../../../../../../core/models/commerce/cards-on-file/card-on-file-response';
import { FormGroupType } from '../../../../../../../core/models/form-group-type';
import { RegExPattern } from '../../../../../../../core/models/regexp-pattern/regex-pattern';
import { CardOnFileFormConstants } from '../../shared/constants/card-on-file-form-constants';
import { CardOnFileFormService } from '../../shared/services/card-on-file-form.service';
import { EditCardOnFileFormModel } from '../models/edit-card-on-file-form-model';

@Injectable({
    providedIn: 'root',
})
export class EditCardOnFileFormService {
    private readonly cardOnFileFormService = inject(CardOnFileFormService);

    /**
     * Defines the form structure and validation rules for the card on file form.
     */
    getFormDefinition(): FormGroup<FormGroupType<EditCardOnFileFormModel>> {
        const form = new FormGroup<FormGroupType<EditCardOnFileFormModel>>({
            address: new FormControl<string | null>(null, [
                Validators.required,
                Validators.maxLength(
                    CardOnFileFormConstants.ADDRESS_MAX_LENGTH,
                ),
                Validators.pattern(
                    RegExPattern.PrintableAsciiExceptAngleBracketsAndPipes,
                ),
            ]),
            city: new FormControl<string | null>(null, [
                Validators.required,
                Validators.maxLength(CardOnFileFormConstants.CITY_MAX_LENGTH),
                Validators.pattern(
                    RegExPattern.PrintableAsciiExceptAngleBracketsAndPipes,
                ),
            ]),
            state: new FormControl<string | null>(null, [Validators.required]),
            zip: new FormControl<string | null>(null, [
                Validators.required,
                Validators.maxLength(CardOnFileFormConstants.ZIP_MAX_LENGTH),
                Validators.pattern(RegExPattern.PostalCode),
            ]),
            expirationMonth: new FormControl<string | null>(null, [
                Validators.required,
                this.cardOnFileFormService.expirationMonthValidator(),
            ]),
            expirationYear: new FormControl<string | null>(null, [
                Validators.required,
            ]),
            isDefault: new FormControl<boolean>(false),
        });

        return form;
    }

    /**
     * Patches the form with the existing card on file data to populate the form fields for editing.
     */
    patchForm(
        form: FormGroup<FormGroupType<EditCardOnFileFormModel>>,
        response: CardOnFileResponse,
    ): void {
        form.patchValue({
            ...response,
            expirationMonth: formatNumericalMonthToMM(response.expirationMonth),
            expirationYear: response.expirationYear.toString(),
        });
    }
}
